// src/features/locations/UtilizationTab.jsx
//
// SCORE-HERO-NORMALIZE: Thin orchestrator that places the Utilization KPI hero
// strip ABOVE the UtilizationPanel body — matching the layout of every other
// location tab (Lease, Experience, Service).
//
// Derives utilScore and metrics from the same exported pure helpers that
// UtilizationPanel uses internally, so there is no duplicated logic.

import React from "react";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { SharingRatioValue } from "../../components/SharingRatioValue.jsx";
import { UtilizationPanel, computeMetrics, monthLabel } from "./UtilizationPanel.jsx";

export function UtilizationTab({ location }) {
  if (!location) return null;

  const rows      = Array.isArray(location.utilization) ? location.utilization : [];
  const capacity  = location.installedCapacity > 0 ? location.installedCapacity : 0;
  const headcount = location.latestHeadcount ?? null;
  // OFFICES-PEAK-ATTENDANCE-PROVENANCE: suppress fabricated seed peak while
  // the badge integration is pending.  Server injects this flag on every
  // location row (list + detail).
  const badgeIntegrationPending = !!location.badgeIntegrationPending;

  const hasData = rows.length > 0;
  const metrics = computeMetrics(rows, capacity, headcount);

  // UTIL-PERIOD-LABEL: derive the date-range label from the actual data window
  // so each KPI tile shows its period context (e.g. "Oct '25 – Jun '26").
  const periodSub = rows.length > 1
    ? `${monthLabel(rows[0].month)} – ${monthLabel(rows[rows.length - 1].month)}`
    : rows.length === 1 ? monthLabel(rows[0].month) : null;

  const kpis = [
    {
      testid: "util-kpi-avg",
      label:  "Avg utilization",
      value:  metrics.avgUtilPct != null ? `${metrics.avgUtilPct}%` : "—",
      sub:    periodSub ? `of capacity · ${periodSub}` : "of installed capacity",
      color:  T.blue,
    },
    {
      testid: "util-kpi-peak",
      label:  "Peak utilization",
      value:  badgeIntegrationPending ? "—" : (metrics.peakUtilPct != null ? `${metrics.peakUtilPct}%` : "—"),
      sub:    badgeIntegrationPending ? "Awaiting badge feed" : (periodSub ? `highest day · ${periodSub}` : "highest single day"),
      color:  T.gold,
    },
    {
      testid: "util-kpi-sharing",
      label:  "Sharing ratio",
      value:  headcount != null && metrics.sharingRatio != null
        ? <SharingRatioValue numerator={metrics.sharingRatio.toFixed(2)} />
        : "—",
      sub:    periodSub
        ? <><span>assigned headcount / seat</span><span> · {periodSub}</span></>
        : "assigned headcount / seat",
      color:  T.teal,
    },
    {
      testid: "util-kpi-attendance",
      label:  "Avg attendance",
      value:  metrics.avgMean != null ? Math.round(metrics.avgMean).toLocaleString() : "—",
      sub:    periodSub ? `daily avg · ${periodSub}` : "people, daily avg",
      color:  T.purple,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: S.paneGap }}>
      <p data-testid="utilization-definition" style={{
        margin: 0, color: T.mid, fontSize: T.fsSm5, lineHeight: 1.5,
      }}>
        Attendance is people present divided by assigned headcount; seat utilization is people present divided by installed seats.
        This view uses Monday to Friday selected-feed observations, excluding Ripple-wide closures and holidays listed for this office{"'"}s country.
        A missing feed day is not counted as zero.
      </p>
      {/* ── KPI tile strip — first element, flush with page gutter ────── */}
      {hasData && (
        <div
          data-testid="util-kpi-strip"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(140px, 1fr))",
            gap: 12,
          }}
        >
          {kpis.map((kpi, i) => (
            <KpiTile
              key={kpi.testid}
              testId={kpi.testid}
              label={kpi.label}
              value={kpi.value}
              sub={kpi.sub}
              color={kpi.color}
              tileIndex={i}
            />
          ))}
        </div>
      )}

      {/* ── Utilization badges panel (heading · chart · waffle) ────────── */}
      <div data-testid="util-badges-panel" style={{ display: "flex", flexDirection: "column", gap: S.paneGap }}>
        <UtilizationPanel location={location} />
      </div>
    </div>
  );
}
